import { Link } from "react-router-dom";
import { COMPANY, PLATFORM_LINKS } from "../lib/company";
import { BrandLockup } from "./BrandMark";
import { EmailLink } from "./EmailLink";
import { FacebookIcon, InstagramIcon, YouTubeIcon } from "./PlatformIcons";
import { RESOURCE_ARTICLES } from "../resources/catalog";

const RESOURCE_GROUPS = [
  { title: "Everyday publishing", subjects: ["Buffer", "Meta Business Suite", "Loomly"] },
  { title: "Creators & insights", subjects: ["Later", "Tailwind", "Metricool"] },
  { title: "Teams & agencies", subjects: ["Agorapulse", "eClincher", "Hootsuite", "Sked Social", "Sprout Social"] },
];

/** Global footer for every public page. Carries the disclosures App Review looks for. */
export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="site-footer-wrap">
        <div className="site-footer-inner">
          <div className="site-footer-col brand-col">
            <div className="site-brand footer-brand">
              <BrandLockup />
            </div>
            <p>
              AI-Assisted. Human-Led. Social media management for planning,
              creating, scheduling and publishing from one place.
            </p>
            <p className="footer-madeby">
              <strong>{COMPANY.product}</strong> is a product of{" "}
              <strong>{COMPANY.legalName}</strong>.
            </p>
          </div>

          <div className="site-footer-col footer-links-col">
            <h3><i aria-hidden="true">◇</i>Product</h3>
            <Link to="/">Social media management</Link>
            <Link to="/social-media-scheduler">Social media scheduler</Link>
            <Link to="/how-it-works">How it works</Link>
            <Link to="/resources">Resources &amp; comparisons</Link>
            <Link to="/partners/content-creator">Content creators</Link>
            <Link to="/security">Security</Link>
            <Link to="/support">Support</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/about">About us</Link>
            <Link to="/signin">Sign in</Link>
            <Link to="/app/">Mobile App</Link>
          </div>

          <div className="site-footer-col footer-links-col">
            <h3><i aria-hidden="true">♢</i>Legal</h3>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/data-deletion">Delete your data</Link>
          </div>

          <div className="site-footer-col company-col">
            <h3><i aria-hidden="true">♧</i>Company</h3>
            <address>
              <strong>{COMPANY.legalName}</strong><br />
              {COMPANY.address.line1}<br />
              {COMPANY.address.line2}<br />
              {COMPANY.address.city}, {COMPANY.address.state} {COMPANY.address.postalCode}<br />
              {COMPANY.address.country}
            </address>
            <div className="footer-contact"><i aria-hidden="true">✉</i><EmailLink address={COMPANY.supportEmail} /></div>
            <div className="footer-contact"><i aria-hidden="true">▤</i><span>GST: {COMPANY.gstin}</span></div>
            <div className="footer-contact"><i aria-hidden="true">☏</i><a href={`tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`}>
              {COMPANY.phone}
            </a></div>
          </div>
        </div>

        <nav className="footer-resources-card" aria-labelledby="footer-resources-title">
          <div className="footer-resources-head">
            <div>
              <span className="eyebrow">Learn. Compare. Plan ahead.</span>
              <h2 id="footer-resources-title">Resources for your next move.</h2>
              <p>Practical guides and thoughtful comparisons to help you find your fit.</p>
            </div>
            <Link to="/resources" className="footer-resources-browse">Explore all resources <span aria-hidden="true">→</span></Link>
          </div>
          <div className="footer-resources-grid">
            <div className="footer-resources-group">
              <h3><span aria-hidden="true">◷</span>Guides &amp; ideas</h3>
              <ul>
                <li><Link to="/resources/why-scheduling">Why scheduling matters</Link></li>
                {RESOURCE_ARTICLES.filter(article => article.category === "Product direction").map(article => (
                  <li key={article.slug}><Link to={`/resources/${article.slug}`}>{article.title}</Link></li>
                ))}
              </ul>
            </div>
            {RESOURCE_GROUPS.map(group => (
              <div className="footer-resources-group" key={group.title}>
                <h3><span aria-hidden="true">◇</span>{group.title}</h3>
                <ul>{RESOURCE_ARTICLES.filter(article => group.subjects.includes(article.subject)).map(article => (
                  <li key={article.slug}><Link to={`/resources/${article.slug}`}>{article.title}</Link></li>
                ))}</ul>
              </div>
            ))}
          </div>
        </nav>

        <div className="footer-platforms-card">
          <div className="footer-platforms" aria-label="Supported platforms">
            <a href={COMPANY.social.facebook} target="_blank" rel="noreferrer noopener">
              <FacebookIcon />Facebook Pages
            </a>
            <span><InstagramIcon />Instagram Business</span>
            <span><YouTubeIcon />YouTube</span>
          </div>
          <p className="footer-madeby footer-credit">
            Made with <span className="footer-heart" aria-hidden="true">♥</span>
            <span className="visually-hidden">love</span> using{" "}
            <a href={COMPANY.studio.url} target="_blank" rel="noreferrer noopener">
              {COMPANY.studio.name} &mdash; {COMPANY.studio.tagline}
            </a>.
          </p>
        </div>

        <div className="footer-divider"><span aria-hidden="true">✦</span></div>

        <div className="site-footer-legal">
          <p><i aria-hidden="true">♢</i><span>
            &copy; {COMPANY.effectiveDate.slice(-4)} {COMPANY.legalName}. All rights reserved.
            Governed by the laws of India; courts at {COMPANY.jurisdiction} have exclusive
            jurisdiction.
          </span></p>
          <p><i aria-hidden="true">♧</i><span>
            {COMPANY.product} is an independent tool. It is not affiliated with, endorsed by
            or sponsored by Meta Platforms, Inc. or Google LLC. Facebook, Instagram, YouTube
            and their logos are trademarks of their respective owners. Your use of the
            connected platforms remains subject to the{" "}
            <a href={PLATFORM_LINKS.metaTerms} target="_blank" rel="noreferrer noopener">Meta Terms</a>,{" "}
            <a href={PLATFORM_LINKS.instagramTerms} target="_blank" rel="noreferrer noopener">Instagram Terms</a> and{" "}
            <a href={PLATFORM_LINKS.youtubeTerms} target="_blank" rel="noreferrer noopener">YouTube Terms of Service</a>.
          </span></p>
          <p><i aria-hidden="true">▢</i><span>
            {COMPANY.product} uses YouTube API Services. Google&rsquo;s{" "}
            <a href={PLATFORM_LINKS.googlePrivacy} target="_blank" rel="noreferrer noopener">Privacy Policy</a>{" "}
            applies to data Google receives, and you can revoke our access at any time from your{" "}
            <a href={PLATFORM_LINKS.googlePermissions} target="_blank" rel="noreferrer noopener">Google security settings</a>.
          </span></p>
        </div>
      </div>
    </footer>
  );
}
